import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BlogIcon from './BlogIcon';
import { BlogLink, isInternalHref, useBlogSettings } from './blogSettings';

export type BlogPageVariant = 'list' | 'detail';

const withAmbassadorLink = (links: BlogLink[]): BlogLink[] =>
  links.some((link) => link.href === '/ambassadors/join')
    ? links
    : [...links, { label: 'سفیر شو', href: '/ambassadors/join' }];

/** لینک فعال: «مقالات» روی همهٔ صفحه‌های وبلاگ؛ بقیه با تطابق مسیر */
const isActive = (href: string, pathname: string) => {
  if (!isInternalHref(href)) return false;
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
};

const NavAnchor: React.FC<{ link: BlogLink; className: string; onClick?: () => void; current?: boolean; children?: React.ReactNode }> = ({
  link,
  className,
  onClick,
  current,
  children,
}) =>
  isInternalHref(link.href) ? (
    <Link to={link.href} className={className} onClick={onClick} aria-current={current ? 'page' : undefined}>
      {children ?? link.label}
    </Link>
  ) : (
    <a href={link.href} className={className} onClick={onClick}>
      {children ?? link.label}
    </a>
  );

/** منوی کشویی موبایل (طرح «Mobile Open Drawer Menu») */
const MobileDrawer: React.FC<{ open: boolean; onClose: () => void; loginHref: string; loginLabel: string }> = ({ open, onClose, loginHref, loginLabel }) => {
  const settings = useBlogSettings();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="منوی ناوبری">
      <button type="button" aria-label="بستن منو" className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-[320px] max-w-[88vw] flex-col overflow-hidden border-l border-[#e6e4df] bg-white shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)]">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-[#e6e4df] bg-[#faf9f6] px-4">
          <span className="text-[16px] font-extrabold leading-6 text-[#4a2e68]">{settings.brandName}</span>
          <button type="button" onClick={onClose} aria-label="بستن منو" className="flex h-11 w-11 items-center justify-center rounded-[8px] hover:bg-[#efeeeb]">
            <BlogIcon name="close-20" width={20} height={20} />
          </button>
        </div>
        <nav className="flex flex-col gap-1 p-4" aria-label="ناوبری اصلی">
          {withAmbassadorLink(settings.navLinks).map((link) => {
            const active = isActive(link.href, pathname);
            return (
              <NavAnchor
                key={`${link.label}-${link.href}`}
                link={link}
                onClick={onClose}
                current={active}
                className={`rounded-[8px] px-3 py-[10px] text-right text-[14px] leading-5 ${
                  active ? 'bg-[#4a2e68] font-bold text-white' : 'font-medium text-[#222] hover:bg-[#f4f3f1]'
                }`}
              />
            );
          })}
        </nav>
        <div className="mt-auto border-t border-[#e6e4df] bg-[#f4f3f1] px-4 pb-4 pt-4">
          <NavAnchor
            link={{ label: loginLabel, href: loginHref }}
            onClick={onClose}
            className="flex h-11 w-full items-center justify-center rounded-[8px] bg-[#4a2e68] text-[12px] font-bold leading-4 text-white hover:bg-[#392352]"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * هدر اختصاصی وبلاگ طبق طرح؛ صفحهٔ فهرست و صفحهٔ مقاله هر کدام نسخهٔ خودشان را دارند.
 * دسکتاپ: لوگو و ناوبری راست، دکمهٔ ورود چپ. موبایل: لوگو راست، دکمهٔ منو چپ.
 */
const BlogHeader: React.FC<{ variant: BlogPageVariant }> = ({ variant }) => {
  const settings = useBlogSettings();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isDetail = variant === 'detail';

  // کاربر واردشده به داشبورد خودش می‌رود
  const loginHref = user ? '/dashboard' : settings.loginHref;
  const loginLabel = user ? 'حساب کاربری' : settings.loginLabel;
  const drawerLoginLabel = user ? 'حساب کاربری' : settings.mobileMenuLoginLabel;

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-50">
      {/* دسکتاپ و تبلت */}
      <div className="hidden border-b border-[#e9e8e5] bg-[#faf9f6] px-6 lg:block xl:px-10">
        <div className="mx-auto flex h-16 max-w-[1200px] items-center justify-between px-8">
          <div className={`flex items-center ${isDetail ? 'gap-10' : 'gap-8'}`}>
            <Link
              to="/articles"
              className={`font-bold text-[#331751] ${isDetail ? 'text-[22px] leading-9 tracking-[-0.55px]' : 'text-[24px] leading-8 tracking-[-0.6px]'}`}
            >
              {settings.brandName}
            </Link>
            <nav className="flex items-center gap-4 xl:gap-6" aria-label="ناوبری اصلی">
              {withAmbassadorLink(settings.navLinks).map((link) => {
                const active = isActive(link.href, pathname);
                return (
                  <NavAnchor
                    key={`${link.label}-${link.href}`}
                    link={link}
                    current={active}
                    className={`text-[14px] transition-colors ${isDetail ? 'leading-6' : 'leading-5'} ${
                      active
                        ? 'border-b-2 border-[#331751] pb-1 font-bold text-[#331751]'
                        : `rounded-[4px] font-medium hover:text-[#331751] ${isDetail ? 'text-[#5b5f66]' : 'text-[#52575e]'}`
                    }`}
                  />
                );
              })}
            </nav>
          </div>
          {isDetail ? (
            <NavAnchor
              link={{ label: loginLabel, href: loginHref }}
              className="flex items-center gap-[6px] rounded-[8px] bg-[#331751] px-4 py-2 text-[14px] font-medium leading-6 text-white transition-colors hover:bg-[#392352]"
            >
              <BlogIcon name="login" width={13.5} height={13.5} />
              {loginLabel}
            </NavAnchor>
          ) : (
            <NavAnchor
              link={{ label: loginLabel, href: loginHref }}
              className="rounded-[8px] border border-[#cdc3d0] px-4 py-[6px] text-center text-[14px] font-medium leading-5 text-[#331751] transition-colors hover:bg-[#f4effa]"
            />
          )}
        </div>
      </div>

      {/* موبایل */}
      <div className="border-b border-[#e9e8e5] bg-[rgba(250,249,246,0.95)] backdrop-blur-[6px] lg:hidden">
        {/* ارتفاع طرح: فهرست ۵۶+۱، مقاله ۵۵+۱ */}
        <div className={`flex items-center justify-between px-4 ${isDetail ? 'h-[55px]' : 'h-14'}`}>
          <Link to="/articles" className="flex items-center gap-[6px]" aria-label={settings.brandName}>
            {isDetail && <span className="h-2 w-2 shrink-0 rounded-full bg-[#4a2e68]" aria-hidden />}
            <span className={`text-[22px] font-bold leading-9 text-[#331751] ${isDetail ? '' : 'tracking-[-0.55px]'}`}>{settings.brandName}</span>
            {!isDetail && (
              <span className="flex h-[10px] w-[6px] shrink-0 items-start pb-1" aria-hidden>
                <span className="h-[6px] w-[6px] rounded-full bg-[#4a2e68]" />
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="باز کردن منوی ناوبری"
            aria-expanded={open}
            className="flex h-11 w-11 items-center justify-center rounded-[8px] hover:bg-[#efeeeb]"
          >
            {isDetail ? <BlogIcon name="menu-sm" width={18} height={12} /> : <BlogIcon name="menu" width={19.5} height={13} />}
          </button>
        </div>
      </div>

      <MobileDrawer open={open} onClose={() => setOpen(false)} loginHref={loginHref} loginLabel={drawerLoginLabel} />
    </header>
  );
};

export default BlogHeader;
